import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useMemo } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_ROLES_LIST,
  type GetRolesListQueryResult,
  type GetRolesListQueryVariables,
  type Role,
  type UserGroupWhere,
} from '../../modules/roles/operations.js';
import { makeRoleColumns } from '../../modules/roles/role-columns.js';

// Column id -> Prisma orderBy path, verified against SORTABLE_FIELDS.UserGroup.
export const ROLE_SORT_MAP: Record<string, string[]> = {
  name: ['name'],
  activeFlag: ['activeFlag'],
};

const ROLES_WHERE: UserGroupWhere = { activeFlag: { equals: true } };

export const Route = createFileRoute('/roles/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: SetupRolesPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function SetupRolesPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('userGroup:edit');
  const columns = useMemo(() => makeRoleColumns(navigate, canEdit), [navigate, canEdit]);

  const {
    rows: roles,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<GetRolesListQueryResult, GetRolesListQueryVariables, Role, UserGroupWhere>({
    query: GET_ROLES_LIST,
    extractConnection: (data) => data?.userGroups,
    defaultSort: [{ id: 'name', desc: false }],
    sortMap: ROLE_SORT_MAP,
    sortTiebreakerField: null, // Roles are `userGroups` under the hood, and UserGroupOrderBy has no `id` field yet.
    where: ROLES_WHERE,
  });

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Roles</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Define roles and the permissions granted to each.
        </DimeCardDescription>
        <DimeCardAction>
          <PermissionGuard
            required={['userGroup:edit']}
            pass={
              <Link to="/roles/new" className={buttonVariants({})}>
                + New Role
              </Link>
            }
            fail={
              <span className={buttonVariants({})} role="link" aria-disabled="true">
                + New Role
              </span>
            }
          />
        </DimeCardAction>
      </DimeCardHeader>

      {error && roles.length === 0 ? (
        <ListErrorState
          title="Could not load roles"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : !loading && roles.length === 0 && !search ? (
        <ListEmptyState
          icon={<ShieldCheck />}
          title="No roles yet"
          description="Get started by creating your first role."
          action={
            <PermissionGuard
              required={['userGroup:edit']}
              pass={
                <Link to="/roles/new" className={buttonVariants({ size: 'sm' })}>
                  + New Role
                </Link>
              }
              fail={
                <span className={buttonVariants({ size: 'sm' })} role="link" aria-disabled="true">
                  + New Role
                </span>
              }
            />
          }
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={roles}
          maxBodyHeight="viewport"
          searchColumn="name"
          searchPlaceholder="Search by name…"
          onRowClick={(role) =>
            navigate({
              to: '/roles/$roleId',
              params: { roleId: role.id },
              search: { edit: false },
            })
          }
          showViewOptions
          emptyMessage="No roles found. Try adjusting your search."
          loadingRowCount={tableProps.pageSize}
          exportFileName="roles"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
